/**
 * Keeps the ticker wider than the viewport.
 *
 * The loop works by rendering the message list twice and translating the track
 * by -50%, so one cycle travels exactly one pass. If a pass is narrower than
 * the viewport the tail runs out before the wrap and a blank stretch appears on
 * the right.
 *
 * A pass's width depends on the rendered text, which PHP cannot know, so it is
 * measured here and the list repeated until one cycle's travel covers the
 * viewport. The server still renders two passes, so the block works without
 * this script — it can just leave a gap on a wide screen.
 */

import domReady from '@wordpress/dom-ready';

const RESIZE_DEBOUNCE_MS = 150;
const MIN_PASSES_PER_CYCLE = 1;
// The track always holds two cycles' worth: one on screen, one to wrap into.
const PASSES_PER_TRACK = 2;

function readPass(track) {
  // The server renders the list exactly twice; the first half is the real one.
  if (!track.passNodes) {
    const children = Array.from(track.children);
    const perPass = children.length / PASSES_PER_TRACK;

    if (!Number.isInteger(perPass) || perPass < 1) {
      return null;
    }

    track.passNodes = children
      .slice(0, perPass)
      .map((node) => node.cloneNode(true));
  }

  return track.passNodes;
}

function cloneForLoop(node) {
  const copy = node.cloneNode(true);

  // Duplicates must not be read out or focusable — a focusable element inside
  // aria-hidden is an accessibility error.
  copy.setAttribute('aria-hidden', 'true');
  copy.querySelectorAll('a').forEach((link) => {
    link.setAttribute('tabindex', '-1');
  });

  return copy;
}

function fit(ticker) {
  const track = ticker.querySelector('.ticker-track');

  if (!track) {
    return;
  }

  const passNodes = readPass(track);

  if (!passNodes || passNodes.length === 0) {
    return;
  }

  // A ticker that is not laid out yet measures zero; cloning off that would
  // produce a wrong number of copies.
  if (!ticker.clientWidth) {
    return;
  }

  const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
  const passWidth = Array.from(track.children)
    .slice(0, passNodes.length)
    .reduce((total, node) => total + node.getBoundingClientRect().width + gap, 0);

  if (!passWidth) {
    return;
  }

  // One cycle travels `passesPerCycle` passes; the track holds twice that, so
  // there is always a full viewport of content left when the loop wraps.
  const passesPerCycle = Math.max(
    MIN_PASSES_PER_CYCLE,
    Math.ceil(ticker.clientWidth / passWidth)
  );
  const wantedPasses = passesPerCycle * PASSES_PER_TRACK;

  if (track.children.length === wantedPasses * passNodes.length) {
    return;
  }

  const fragment = document.createDocumentFragment();

  for (let pass = 0; pass < wantedPasses; pass += 1) {
    passNodes.forEach((node) => {
      fragment.appendChild(pass === 0 ? node.cloneNode(true) : cloneForLoop(node));
    });
  }

  track.replaceChildren(fragment);

  // The cycle now covers more ground, so the duration has to grow with it or
  // the ticker would speed up on wider screens.
  ticker.style.setProperty(
    '--ticker-count',
    String(passesPerCycle * passNodes.length)
  );
}

function fitAll() {
  document
    .querySelectorAll('.wp-block-improntad-theme-blocks-ticker')
    .forEach(fit);
}

// Measuring before the layout settles gives the wrong answer, so every
// entry point waits for the thing that would change a text measurement:
//
//   1. the DOM exists at all,
//   2. the webfont has swapped in — a fallback face has different metrics,
//      and measuring against it would size the loop for the wrong text,
//   3. the viewport changed.
//
// Block stylesheets are render-blocking or inlined ahead of the markup, so
// they are already applied by the time any of these fire.
domReady(() => {
  fitAll();

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(fitAll);
  }
});

let resizeTimer = null;
window.addEventListener('resize', () => {
  window.clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(fitAll, RESIZE_DEBOUNCE_MS);
});
